import { useCallback } from "react";
import React from "react";
import { Text, View } from "@react-pdf/renderer";
import { styles, spacing } from "components/Resume/ResumePDF/styles";
import { DEBUG_RESUME_PDF_FLAG } from "lib/constants";
import { handleClick } from "lib/utils/navigator";

// Optimized section component for multi-entry components with advanced orphan prevention
export const TemplateSeventeenSectionWithSubsections = ({
  themeColor,
  heading,
  children,
}: {
  themeColor?: string;
  heading?: string;
  children: React.ReactNode;
}) => {
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  const childrenArray = React.Children.toArray(children);
  
  if (childrenArray.length === 0) {
    return null;
  }

  // First child gets grouped with heading to prevent orphans
  const [firstChild, ...restChildren] = childrenArray;

  return (
    <div
      onClick={handleClickCallback}
      style={{
        cursor: "pointer"
      }}
    >
      {/* Heading + first subsection container to prevent orphans */}
      <View wrap={false} style={{ ...styles.flexCol }}>
        {heading && (
          <View style={{ ...styles.flexCol, gap: spacing["1"], marginTop: spacing["5"] }}>
            <Text
              style={{
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: "black", // Consistent black text
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {heading}
            </Text>
            {themeColor && (
              <View
                style={{
                  height: "1.25pt",
                  width: "100%",
                  backgroundColor: "white",
                  marginRight: spacing["3.5"],
                }}
                debug={DEBUG_RESUME_PDF_FLAG}
              />
            )}
          </View>
        )}
        {firstChild && (
          <View
            style={{
              ...styles.flexCol,
              gap: spacing["2"],
              marginTop: spacing["2"],
            }}
          >
            {firstChild}
          </View>
        )}
      </View>
      {/* Remaining subsections */}
      {restChildren.length > 0 && (
        <View
          style={{
            ...styles.flexCol,
            gap: spacing["2"],
          }}
        >
          {restChildren}
        </View>
      )}
    </div>
  );
};

// Enhanced single-entry section component with orphan prevention
export const TemplateSeventeenSection = ({
  themeColor,
  heading,
  children,
}: {
  themeColor?: string;
  heading?: string;
  children: React.ReactNode;
}) => {
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);

  return (
    <div
      onClick={handleClickCallback}
      style={{
        cursor: "pointer"
      }}
    >
      <View
        wrap={false} // Prevent orphan sections
        style={{
          ...styles.flexCol,
          gap: spacing["2"],
          marginTop: spacing["5"],
        }}
      >
        {heading && (
          <View style={{ ...styles.flexCol, gap: spacing["1"] }}>
            <Text
              style={{
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: "black", // Consistent black text
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {heading}
            </Text>
            {themeColor && (
              <View
                style={{
                  height: "1.25pt",
                  width: "100%",
                  backgroundColor: "white",
                  marginRight: spacing["3.5"],
                }}
                debug={DEBUG_RESUME_PDF_FLAG}
              />
            )}
          </View>
        )}
        {children && (
          <View>
            {children}
          </View>
        )}
      </View>
    </div>
  );
};